import React, { useState, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { PlayCircle, CheckCircle } from 'lucide-react';

interface Video {
  id: string;
  title: string;
  description: string;
  url: string;
  embedId: string;
}

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const videos: Video[] = [
  {
    id: '1',
    title: 'Welcome to ZenHammer',
    description: 'Get started with ZenHammer and learn the basics of construction project management.',
    url: 'https://www.youtube.com/watch?v=_MZlXFCD8xM',
    embedId: '_MZlXFCD8xM'
  },
  {
    id: '2',
    title: 'Creating and Editing Tasks',
    description: 'Learn how to create, assign, and manage tasks in your construction projects.',
    url: 'https://www.youtube.com/watch?v=ZC_eJYASZos',
    embedId: 'ZC_eJYASZos'
  },
  {
    id: '3',
    title: 'Understanding User Hierarchies, Roles, and Permissions',
    description: 'Master user management with roles, permissions, and organizational hierarchies.',
    url: 'https://www.youtube.com/watch?v=qGD5huRck-A',
    embedId: 'qGD5huRck-A'
  }
];

const STORAGE_KEY = 'tutorialWatchedVideos';

export const TutorialModal: React.FC<TutorialModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [watchedVideos, setWatchedVideos] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const currentVideo = videos[currentVideoIndex];

  const handleVideoSelect = (index: number) => {
    setCurrentVideoIndex(index);
  };

  const markAsWatched = (videoId: string) => {
    setWatchedVideos(prev => {
      const updated = new Set(Array.from(prev).concat([videoId]));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(updated)));
      return updated;
    });
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isOpen && currentVideoIndex < videos.length - 1) {
      timeoutId = setTimeout(() => {
        markAsWatched(currentVideo.id);
        setCurrentVideoIndex(currentVideoIndex + 1);
      }, 180000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, currentVideoIndex, currentVideo.id]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tutorials"
      size="full"
      showCloseButton={true}
      closeOnOverlayClick={false}
      closeOnEscape={true}
      handleConfirm={onClose}
      confirmText="Close"
      contentClassName="flex flex-col">
      <div className="flex flex-col lg:flex-row gap-4 h-full flex-1 min-h-0">
        {/* Video Player Section */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="bg-black rounded-lg overflow-hidden mb-4 relative aspect-video lg:aspect-auto lg:flex-1">
            <iframe
              key={`video-${currentVideoIndex}`}
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${currentVideo.embedId}?enablejsapi=1&origin=${window.location.origin}`}
              title={currentVideo.title}
              style={{ border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <div className="px-2">
            <h3 className="text-lg font-semibold mb-2 text-gray-900">
              {currentVideo.title}
            </h3>
            <p className="text-sm text-gray-600">
              {currentVideo.description}
            </p>
          </div>
        </div>

        {/* Playlist Section */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-200 pt-4 lg:pt-0 lg:pl-4 flex flex-col min-h-0">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Playlist ({videos.length} videos)
          </h3>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-2">
            {videos.map((video, index) => (
              <div
                key={video.id}
                onClick={() => handleVideoSelect(index)}
                className={`rounded-lg cursor-pointer transition-all duration-200 p-3 border ${
                  index === currentVideoIndex
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-transparent hover:bg-gray-50'
                }`}>
                <div className="flex items-start gap-3 w-full">
                  <div
                    className="flex-shrink-0 mt-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsWatched(video.id);
                    }}>
                    {watchedVideos.has(video.id) ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <PlayCircle
                        className={`w-5 h-5 ${
                          index === currentVideoIndex ? 'text-blue-500' : 'text-gray-400'
                        }`}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`text-sm font-medium leading-tight ${
                      index === currentVideoIndex ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {video.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Progress indicator */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              Progress
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(watchedVideos.size / videos.length) * 100}%`
                  }}
                />
              </div>
              <span className="text-xs text-gray-600">
                {watchedVideos.size}/{videos.length}
              </span>
            </div>

            {/* Manual mark as watched button */}
            <div className="mt-3 space-y-2">
              <button type="button"
                onClick={() => markAsWatched(currentVideo.id)}
                disabled={watchedVideos.has(currentVideo.id)}
                className={`w-full py-2 px-3 rounded text-sm font-medium transition-colors ${
                  watchedVideos.has(currentVideo.id)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}>
                {watchedVideos.has(currentVideo.id) ? 'Watched ✓' : 'Mark as Watched'}
              </button>

              {/* Next Video Button */}
              {currentVideoIndex < videos.length - 1 && (
                <button type="button"
                  onClick={() => {
                    markAsWatched(currentVideo.id);
                    setCurrentVideoIndex(currentVideoIndex + 1);
                  }}
                  className="w-full py-2 px-3 rounded text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-colors">
                  Next Video →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
